// Date formatting functions
export function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return 'N/A'
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

export function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return 'N/A'
  // formatDateTimeDisplay is imported from @applicator/shared
  const { formatDateTimeDisplay } = require('@applicator/shared')
  return formatDateTimeDisplay(dateStr) === 'Not set' ? 'N/A' : formatDateTimeDisplay(dateStr)
}

// Helper to capitalize first letter
export function capitalizeStatus(status: string): import('@applicator/shared').JobStatus {
  if (!status) return 'Bookmarked'
  return (status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()) as import('@applicator/shared').JobStatus
}

// Format time ago functions
export function formatDaysAgo(diffDays: number): string {
  return diffDays === 1 ? 'a day ago' : `${diffDays} days ago`
}

export function formatHoursAgo(diffHours: number): string {
  return diffHours === 1 ? 'an hour ago' : `${diffHours} hours ago`
}

export function formatMinutesAgo(diffMinutes: number): string {
  return diffMinutes === 1 ? 'a minute ago' : `${diffMinutes} minutes ago`
}

export function formatTimeDifference(diffDays: number, diffHours: number, diffMinutes: number): string {
  if (diffDays > 0) {
    return formatDaysAgo(diffDays)
  }
  if (diffHours > 0) {
    return formatHoursAgo(diffHours)
  }
  if (diffMinutes > 0) {
    return formatMinutesAgo(diffMinutes)
  }
  return 'just now'
}

export function calculateTimeDifferences(diffMs: number) {
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)
  return { diffDays, diffHours, diffMinutes }
}

export function isValidDateString(savedAt: string | undefined | null): boolean {
  return !!(savedAt && typeof savedAt === 'string')
}

export function createValidDate(dateStr: string): Date | null {
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) {
    return null
  }
  return date
}

export function createCurrentDate(): Date | null {
  const now = new Date()
  if (isNaN(now.getTime())) {
    return null
  }
  return now
}

export function validateAndParseDate(savedAt: string | undefined | null): { savedDate: Date; now: Date } | null {
  if (!isValidDateString(savedAt)) {
    return null
  }
  
  const savedDate = createValidDate(savedAt as string)
  if (!savedDate) {
    return null
  }
  
  const now = createCurrentDate()
  if (!now) {
    return null
  }
  
  return { savedDate, now }
}

export function calculateTimeDiffMs(savedDate: Date, now: Date): number | null {
  const diffMs = now.getTime() - savedDate.getTime()
  if (isNaN(diffMs)) {
    return null
  }
  return diffMs
}

export function processDateDataForFormatting(dateData: { savedDate: Date; now: Date } | null): { diffDays: number; diffHours: number; diffMinutes: number } | null {
  if (!dateData) {
    return null
  }
  
  const { savedDate, now } = dateData
  const diffMs = calculateTimeDiffMs(savedDate, now)
  if (diffMs === null) {
    return null
  }
  
  return calculateTimeDifferences(diffMs)
}

export function formatTimeAgo(savedAt: string | undefined | null): string {
  try {
    const dateData = validateAndParseDate(savedAt)
    const timeDiffs = processDateDataForFormatting(dateData)
    if (!timeDiffs) {
      return ''
    }
    
    const { diffDays, diffHours, diffMinutes } = timeDiffs
    return formatTimeDifference(diffDays, diffHours, diffMinutes)
  } catch (error) {
    return ''
  }
}

export function formatDateParts(date: Date): { day: string; month: string; year: number; hours: string; minutes: string } {
  return {
    day: String(date.getDate()).padStart(2, '0'),
    month: String(date.getMonth() + 1).padStart(2, '0'),
    year: date.getFullYear(),
    hours: String(date.getHours()).padStart(2, '0'),
    minutes: String(date.getMinutes()).padStart(2, '0'),
  }
}

export function formatFullDateTime(savedAt: string | undefined | null): string {
  try {
    if (!isValidDateString(savedAt)) {
      return ''
    }
    
    const savedDate = createValidDate(savedAt as string)
    if (!savedDate) {
      return ''
    }
    
    const { day, month, year, hours, minutes } = formatDateParts(savedDate)
    return `${day}/${month}/${year}, ${hours}:${minutes}`
  } catch (error) {
    return ''
  }
}
